import React, { useState, useRef, useEffect } from "react";

interface OptionData {
  value:    string;
  label:    React.ReactNode;
  disabled: boolean;
}

export interface SelectProps {
  value:      string;
  onChange?:  React.ChangeEventHandler<HTMLSelectElement>;
  disabled?:  boolean;
  /** Applied to the wrapper div — use for width constraints (e.g. "w-44", "max-w-xs") */
  className?: string;
  children:   React.ReactNode;
  id?:        string;
  /** "default" matches the .input height (min-h-[44px]); "sm" is compact */
  size?:      "default" | "sm";
}

export function Select({
  value,
  onChange,
  disabled  = false,
  className = "",
  children,
  id,
  size      = "default",
}: SelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef        = useRef<HTMLDivElement>(null);

  const options: OptionData[] = [];
  React.Children.forEach(children, (child) => {
    if (React.isValidElement(child) && child.type === "option") {
      const p = child.props as { value?: unknown; children?: React.ReactNode; disabled?: boolean };
      options.push({
        value:    String(p.value ?? ""),
        label:    p.children,
        disabled: Boolean(p.disabled),
      });
    }
  });

  const selectedOption = options.find((o) => o.value === String(value));
  const isPlaceholder  = !selectedOption || selectedOption.value === "";

  const handleSelect = (optionValue: string) => {
    if (onChange) {
      onChange({ target: { value: optionValue } } as React.ChangeEvent<HTMLSelectElement>);
    }
    setIsOpen(false);
  };

  useEffect(() => {
    if (!isOpen) return;
    const onDown = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) setIsOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setIsOpen(false); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [isOpen]);

  const sizeClasses = size === "sm"
    ? "py-1.5 px-3 text-sm"
    : "min-h-[44px] py-2.5 px-3 text-base";

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <button
        type="button"
        id={id}
        disabled={disabled}
        onClick={() => !disabled && setIsOpen((o) => !o)}
        className={[
          "flex w-full items-center justify-between gap-2 rounded-lg border bg-white text-left",
          sizeClasses,
          isOpen
            ? "border-brand-400 ring-2 ring-brand-400 border-transparent"
            : "border-gray-200 hover:border-gray-300",
          disabled ? "cursor-not-allowed bg-gray-50 text-gray-500" : "cursor-pointer",
          "focus:outline-none focus:ring-2 focus:ring-brand-400 focus:border-transparent",
        ].join(" ")}
      >
        <span className={`truncate ${isPlaceholder ? "text-gray-400" : "text-gray-900"}`}>
          {selectedOption?.label ?? " "}
        </span>
        <svg
          className={`shrink-0 h-4 w-4 text-gray-400 transition-transform duration-150 ${isOpen ? "rotate-180" : ""}`}
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M4 6l4 4 4-4" />
        </svg>
      </button>

      {isOpen && (
        <ul
          role="listbox"
          className="absolute z-50 mt-1 w-full max-h-60 overflow-auto rounded-lg bg-white border border-gray-200 shadow-lg py-1 focus:outline-none"
        >
          {options.map((opt, i) => {
            const isSelected = opt.value === String(value);
            return (
              <li
                key={`${opt.value}-${i}`}
                role="option"
                aria-selected={isSelected}
                aria-disabled={opt.disabled}
                onClick={() => !opt.disabled && handleSelect(opt.value)}
                className={[
                  "flex items-center justify-between px-3 py-2 text-sm select-none",
                  opt.disabled
                    ? "text-gray-300 cursor-not-allowed"
                    : isSelected
                      ? "bg-brand-50 text-brand-800 font-medium cursor-pointer"
                      : "text-gray-900 cursor-pointer hover:bg-gray-50",
                ].join(" ")}
              >
                <span className="truncate">{opt.label}</span>
                {isSelected && !opt.disabled && (
                  <svg
                    className="shrink-0 h-4 w-4 text-brand-400 ml-2"
                    viewBox="0 0 16 16"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M3 8l4 4 6-7" />
                  </svg>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
